/**
 * One Classroom teacher or student in the intake review: the Classroom name
 * and email as evidence, what IMAM already knows, and the registrar's
 * decision. A verified row is linked unless the registrar skips it; a row
 * IMAM cannot place waits for an explicit choice; an ambiguous row is held
 * for repair in People.
 */
import { useId } from "react";
import { Link } from "react-router";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { ClassroomIntakeAmbiguousPerson, ClassroomIntakePersonRow } from "@/lib/api/classroomIntake";
import { HONORIFICS } from "@/lib/honorifics";
import { personName } from "@/lib/person-name";
import { PROTECTED_ROUTES } from "@/navigation/routes";

import { IntakePersonSearch } from "./IntakePersonSearch";
import { SectionRepairVisit } from "../course/SectionRepairVisit";
import {
  createdName,
  rowName,
  studentRecordHold,
  studentRecordHoldLine,
  type RowChoice,
  type StudentRecordHold,
} from "./intake-plan";

type CreateChoice = Extract<RowChoice, { decision: "create" }>;

type Option = { value: RowChoice["decision"]; label: string };

const VERIFIED_OPTIONS: readonly Option[] = [
  { value: "link_verified", label: "Link" },
  { value: "skip", label: "Skip" },
];
const CHOICE_OPTIONS: readonly Option[] = [
  { value: "create", label: "Create new" },
  { value: "link_person", label: "Choose a person" },
  { value: "skip", label: "Skip" },
];

const linkClass = "font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function choiceFor(value: RowChoice["decision"]): RowChoice {
  switch (value) {
    case "link_verified": return { decision: "link_verified" };
    case "create": return { decision: "create", honorific: "", givenName: null, familyName: null };
    case "link_person": return { decision: "link_person", person: null };
    case "skip": return { decision: "skip" };
  }
}

export function IntakePersonRow({
  row,
  register,
  choice,
  onChoose,
}: {
  row: ClassroomIntakePersonRow;
  register: "teacher" | "student";
  choice: RowChoice | undefined;
  onChoose: (choice: RowChoice) => void;
  /** The reason a reactivation suggests, naming the term the student continues in. */
  suggestedReason: string;
  /** A linked student record became active; the review reads the course again. */
  onReactivated: () => void;
}) {
  const id = useId();
  const name = rowName(row);
  const disposition = row.disposition;
  const hold = register === "student" ? studentRecordHold(row, choice) : null;

  return (
    <li aria-label={name} className="grid min-w-0 gap-3 border-b border-rule py-4 ps-5 pe-4 last:border-b-0 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] md:gap-6">
      <div className="grid min-w-0 content-start gap-0.5 [overflow-wrap:anywhere]">
        <span className="text-ui font-medium text-foreground">{name}</span>
        {row.email && <span className="text-meta text-muted-foreground">{row.email}</span>}
        {row.is_owner && <span className="text-meta text-muted-foreground">Classroom course owner</span>}
      </div>

      <div className="grid min-w-0 content-start gap-3">
        {disposition.kind === "verified" && (
          <p className="text-ui text-foreground">
            Already in IMAM:{" "}
            <SectionRepairVisit from="intake-confirm" address={`person:${disposition.person_id}`}>{personName(disposition.person_name)}</SectionRepairVisit>
            {register === "student" && disposition.sid && (
              <span className="text-muted-foreground"> · SID <span className="font-mono tabular-nums">{disposition.sid}</span></span>
            )}
            {register === "student" && disposition.enrolled_in_target && (
              <span className="text-muted-foreground"> · Already enrolled</span>
            )}
          </p>
        )}

        {disposition.kind === "ambiguous" && <AmbiguousRow people={disposition.people} />}

        {disposition.kind !== "ambiguous" && (
          <RadioGroup
            aria-label={`Decision for ${name}`}
            value={choice?.decision ?? ""}
            onValueChange={(value) => onChoose(choiceFor(value as RowChoice["decision"]))}
            className="flex flex-wrap gap-x-4 gap-y-1"
          >
            {(disposition.kind === "verified" ? VERIFIED_OPTIONS : CHOICE_OPTIONS).map((option) => (
              <Label key={option.value} htmlFor={`${id}-${option.value}`} className="flex min-h-11 cursor-pointer items-center gap-1 pe-2 text-ui font-normal text-foreground">
                <RadioGroupItem id={`${id}-${option.value}`} value={option.value} />
                {option.label}
              </Label>
            ))}
          </RadioGroup>
        )}

        {choice?.decision === "create" && (
          <div className="grid min-w-0 gap-3">
            <p className="text-meta text-muted-foreground">
              {register === "teacher"
                ? `Creates ${name} as a new person with a teaching profile, assignable before they sign in.`
                : `Records ${name} as a new student with an IMAM student ID, placed as set for new students below.`}
            </p>
            <CreatedName row={row} name={name} choice={choice} onChoose={onChoose} />
            {register === "teacher" && (
              <div className="grid max-w-sm gap-1.5">
                <Label htmlFor={`${id}-honorific`}>Honorific</Label>
                <NativeSelect
                  id={`${id}-honorific`}
                  aria-label={`Honorific for ${name}`}
                  value={choice.honorific}
                  onChange={(event) => onChoose({ ...choice, honorific: event.target.value })}
                >
                  <option value="">None</option>
                  {HONORIFICS.map((honorific) => <option key={honorific} value={honorific}>{honorific}</option>)}
                </NativeSelect>
              </div>
            )}
          </div>
        )}

        {choice?.decision === "link_person" && (
          <IntakePersonSearch
            rowLabel={name}
            register={register}
            person={choice.person}
            onChange={(person) => onChoose({ decision: "link_person", person })}
          />
        )}

        {hold && (
          <StudentRecordHoldNotice hold={hold} />
        )}
      </div>
    </li>
  );
}

/** A linked student record the section cannot take yet: reactivate it here, or restore a withdrawn one on its record. */
function StudentRecordHoldNotice({ hold }: { hold: StudentRecordHold }) {
  return <p>{studentRecordHoldLine(hold)}. The section takes active students only. <SectionRepairVisit from="intake-confirm" address={`person:${hold.person_id}`}>Review the student record</SectionRepairVisit>, then read the intake again.</p>;
}

/** The new person's name, prefilled from Classroom; an edit is sent, an untouched part keeps Classroom's. */
function CreatedName({
  row,
  name,
  choice,
  onChoose,
}: {
  row: ClassroomIntakePersonRow;
  name: string;
  choice: CreateChoice;
  onChoose: (choice: RowChoice) => void;
}) {
  const id = useId();
  const current = createdName(row, choice);
  const parts = [
    { key: "givenName", label: "Given name", value: choice.givenName ?? row.name.given ?? "", empty: !current.given },
    { key: "familyName", label: "Family name", value: choice.familyName ?? row.name.family ?? "", empty: !current.family },
  ] as const;
  return (
    <div className="grid min-w-0 gap-3 sm:max-w-xl sm:grid-cols-2">
      {parts.map((part) => (
        <div key={part.key} className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-${part.key}`}>{part.label}</Label>
          <Input
            id={`${id}-${part.key}`}
            aria-label={`${part.label} for ${name}`}
            autoComplete="off"
            required
            aria-invalid={choice[part.key] !== null && part.empty ? true : undefined}
            value={part.value}
            onChange={(event) => onChoose({ ...choice, [part.key]: event.target.value })}
          />
        </div>
      ))}
    </div>
  );
}

function AmbiguousRow({ people }: { people: ClassroomIntakeAmbiguousPerson[] }) {
  return (
    <div className="grid gap-1 border-l-2 border-status-warning pl-4 text-ui">
      <p className="text-foreground">
        Matches more than one person in IMAM:{" "}
        {people.map((match, index) => (
          <span key={match.person_id}>
            {index > 0 && ", "}
            <SectionRepairVisit from="intake-confirm" address={`person:${match.person_id}`}>{personName(match.name)}</SectionRepairVisit>
          </span>
        ))}
        .
      </p>
      <p className="text-muted-foreground">
        Nothing is linked until the people are repaired.{" "}
        <Link data-nav="" to={PROTECTED_ROUTES.admin.people} className={linkClass}>Open People</Link>
      </p>
    </div>
  );
}
